import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { PageHeader, Panel } from "@/components/layout"
import { Skeleton } from "@/components/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	ProposalScope,
	StatusBadge,
} from "@/features/catalogue-review/ProposalPage"
import { gameName, nf } from "@/features/catalogue-review/review-copy"
import { useProposals } from "@/features/catalogue-review/use-review"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/catalogue-v2/proposals/")({
	component: ProposalsIndex,
})

const COLUMNS = 7

function ProposalsIndex() {
	const proposals = useProposals()
	const navigate = useNavigate()
	const rows = proposals.data ?? []

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Change propositions"
				count={proposals.data ? rows.length : undefined}
				description="Frozen decision sets awaiting review, applied only by someone with the deploy role."
			/>

			<Panel>
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead>Proposition</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Game</TableHead>
							<TableHead>Submitted</TableHead>
							<TableHead className="text-right">Will apply</TableHead>
							<TableHead className="text-right">Flagged</TableHead>
							<TableHead className="text-right">
								Removals touching holdings
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{proposals.isPending
							? [1, 2, 3].map((i) => (
									<TableRow key={i} className="hover:bg-transparent">
										<TableCell colSpan={COLUMNS} className="py-0">
											<Skeleton className="my-2 h-5 w-full" />
										</TableCell>
									</TableRow>
								))
							: null}

						{proposals.data && rows.length === 0 ? (
							<TableRow className="hover:bg-transparent">
								<TableCell colSpan={COLUMNS} className="text-muted-foreground">
									Nothing submitted yet. Decisions become a proposition from a
									game's review page.
								</TableCell>
							</TableRow>
						) : null}

						{rows.map((p) => {
							const open =
								p.status === "submitted" || p.status === "changes_requested"

							return (
								<TableRow
									key={p.id}
									className="cursor-pointer"
									onClick={() =>
										navigate({
											to: "/catalogue-v2/proposals/$proposalId",
											params: { proposalId: p.id },
										})
									}
								>
									<TableCell className="max-w-[28rem]">
										<Link
											to="/catalogue-v2/proposals/$proposalId"
											params={{ proposalId: p.id }}
											className="block truncate font-medium hover:text-primary-text"
											onClick={(e) => e.stopPropagation()}
										>
											{p.title}
										</Link>
										<div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
											<ProposalScope
												gameSlug={p.gameSlug}
												scope={p.expansionScope}
											/>
											{p.stale && p.status === "submitted" ? (
												<span className="text-warn">
													input moved since submit
												</span>
											) : null}
											{p.heldByLock && open ? (
												<span
													className="tabular-nums"
													title={
														p.heldByLockSets?.length
															? `Held because ${p.heldByLockSets.join(", ")} ${p.heldByLockSets.length === 1 ? "is" : "are"} locked`
															: undefined
													}
												>
													{nf(p.heldByLock)} held by locks
												</span>
											) : null}
										</div>
									</TableCell>
									<TableCell>
										<StatusBadge status={p.status} />
									</TableCell>
									<TableCell className="text-muted-foreground">
										{gameName(p.gameSlug)}
									</TableCell>
									<TableCell className="text-xs text-meta tabular-nums">
										{p.createdAt.slice(0, 16).replace("T", " ")}
									</TableCell>
									<TableCell
										className={cn(
											"text-right tabular-nums",
											!p.digest.willApply && "text-meta",
										)}
									>
										{nf(p.digest.willApply)}
									</TableCell>
									<TableCell
										className={cn(
											"text-right tabular-nums",
											p.digest.flaggedGroups ? "text-warn" : "text-meta",
										)}
									>
										{nf(p.digest.flaggedGroups)}
									</TableCell>
									<TableCell
										className={cn(
											"text-right tabular-nums",
											p.digest.riskyRemovals.length ? "text-bad" : "text-meta",
										)}
									>
										{nf(p.digest.riskyRemovals.length)}
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</Panel>
		</div>
	)
}
